import { isNil } from "lodash";
import { clearToken, getToken, getTokenForAddress, setToken } from "./store";
import get from "lodash/get";
import { BigNumber, ethers } from "ethers";
import ENV from "./env";
import { getBackendErrorMessage } from "./backendError";
import { ErrorState } from "../types/mint";

export const getAuthToken = async (
  signIdentity: any,
  address: string,
  signer: any,
  authRequest: any
) => {
  if (isNil(getTokenForAddress(address))) {
    clearToken();
    const signIdentityResponse = await signIdentity.mutateAsync({
      address,
    });
    const nonce = signIdentityResponse?.data.nonce;

    const message = `thedarkhorse.xyz needs to verify that you are the owner of the wallet before showing you any private data. This is a request to sign this message to verify you are the owner of the wallet. This request does NOT spend any ETH and is not shared with anyone.\n\nWallet Address: ${address}\n\nNonce (unique value for you to sign): ${nonce}`;
    const signature = await signer.signMessage(message);

    const authResponse = await authRequest.mutateAsync({
      address,
      nonce,
      signature,
      message,
    });
    setToken(String(authResponse.data), address);
  }
};

export const prepareMintRequest = async (
  address: string,
  amount: number,
  signMintRequest: any
) => {
  return await signMintRequest.mutateAsync({
    address,
    amount,
  });
};

export const isPriceMismatch = (
  mintRequest: any,
  basePrice: BigNumber,
  amount: number
) => {
  const backendPriceWei = get(mintRequest, "data.priceWeiStr");
  const backendPrice = BigNumber.from(backendPriceWei);

  return !basePrice.mul(amount).eq(backendPrice);
};

export const performMint = async (
  contract: any,
  signer: any,
  setAlertState: any,
  mintRequest: any,
  closeAlert: any
) => {
  const abi = contract.abi;

  const nftContract = new ethers.Contract(ENV.CONTRACT_ADDRESS!, abi, signer);
  const { amount, nonce, hashSignature, priceWeiStr } = mintRequest.data;

  console.log("Starting mint");
  const priceWei = ethers.utils.parseUnits(priceWeiStr, "wei");

  let nftTxn = await nftContract.publicMint(amount, nonce, hashSignature, {
    value: priceWei,
  });

  await nftTxn.wait();
  console.log("Minting complete.");

  setAlertState({
    isOpen: true,
    closeAlert,
    message: "Success! NFTs have been minted.",
    severity: "success",
    handleClose: null,
    horizontal: "center",
    vertical: "top",
  });
};

export const hasSufficientFunds = async (
  web3Provider: any,
  price: BigNumber,
  provider: any
) => {
  const accounts = await provider.request({
    method: "eth_requestAccounts",
  });

  const balance: BigNumber = await web3Provider.getBalance(accounts[0]);

  return balance.gte(price);
};

const isInvalidTokenError = (error: any) => {
  const errorMessage = getBackendErrorMessage(error);
  return errorMessage === "Invalid token.";
};

export const prepareMintRequestWithRetry = async (
  address: string,
  amount: number,
  signMintRequest: any,
  signIdentity: any,
  signer: any,
  authRequest: any
) => {
  try {
    return await prepareMintRequest(address, amount, signMintRequest);
  } catch (error) {
    if (!isInvalidTokenError(error)) {
      throw error;
    }
  }

  // retry
  clearToken();
  await getAuthToken(signIdentity, address, signer, authRequest);
  return await prepareMintRequest(address, amount, signMintRequest);
};

const NOT_ON_WHITELIST = "Not on whitelist.";
const WALLET_MINT_LIMIT = "You cannot mint this many NFTs";
const USER_REJECTED_SIGN = "User denied message signature";
const USER_REJECTED_TXN = "User denied transaction signature";

export enum ErrorType {
  NOT_ON_WHITELIST,
  MAX_MINT_ALLOCATION,
  GENERIC_ERROR,
  INSUFFICIENT_FUNDS,
  PRICE_MISMATCH,
  USER_REJECTED_SIGN,
  USER_REJECTED_TXN,
}

export const getErrorType = (error: any): ErrorState => {
  const errorMessage: string = getBackendErrorMessage(error);

  if (errorMessage === NOT_ON_WHITELIST) {
    return { type: ErrorType.NOT_ON_WHITELIST, message: null };
  }

  if (errorMessage.toLowerCase().includes(WALLET_MINT_LIMIT.toLowerCase())) {
    return { type: ErrorType.MAX_MINT_ALLOCATION, message: null };
  }

  if (errorMessage.toLowerCase().includes(USER_REJECTED_SIGN.toLowerCase())) {
    return { type: ErrorType.USER_REJECTED_SIGN, message: null };
  }

  if (errorMessage.toLowerCase().includes(USER_REJECTED_TXN.toLowerCase())) {
    return { type: ErrorType.USER_REJECTED_TXN, message: null };
  }

  return { type: ErrorType.GENERIC_ERROR, message: errorMessage };
};
